import { useLocation, useNavigate } from 'react-router-dom';

import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';

/**
 * A way back, on screens that have no bar to put one in.
 *
 * ── WHY THIS EXISTS SEPARATELY FROM AppBar ──────────────────────────────────
 *
 * Several screens are mounted outside the buyer shell entirely — the pharmacy
 * registration page, the console sign-in doors — because they are not part of
 * the shopping app and must not carry its bottom nav. The side effect was that
 * they had no way back at all: a buyer who tapped "Register your pharmacy" from
 * their account screen, read the page and decided not to, was stranded on it.
 *
 * ── WHY IT CHECKS WHETHER GOING BACK IS EVEN POSSIBLE ───────────────────────
 *
 * `navigate(-1)` on the first page of a session does not go back within
 * ChiCare — it leaves the site, usually to whichever ad or search result sent
 * them. That is a genuinely bad outcome on a page whose entire job is to
 * recruit the person reading it. So when there is no in-app history to return
 * to, this offers a way INTO the app instead of a way out of it.
 *
 * React Router stamps `location.key` as `'default'` only on the entry a session
 * started at, which is exactly the condition worth testing.
 */
export function BackLink({
  fallbackTo = '/',
  fallbackLabel = 'Go to ChiCare',
  label = 'Back',
  className,
}: {
  /** Where to send somebody who arrived here directly. */
  fallbackTo?: string;
  fallbackLabel?: string;
  label?: string;
  className?: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const canGoBack = location.key !== 'default';

  return (
    <button
      type="button"
      onClick={() => (canGoBack ? navigate(-1) : navigate(fallbackTo))}
      className={cn(
        '-ml-2 inline-flex min-h-11 items-center gap-1.5 rounded-[9px] px-2',
        'text-[13px] font-semibold text-muted hover:bg-white hover:text-ink',
        className,
      )}
    >
      <Icon name="back" size={16} strokeWidth={2} />
      {canGoBack ? label : fallbackLabel}
    </button>
  );
}
